import { useLocalStorage } from "@mantine/hooks";
import { cn } from "@win-reality/react-ui";
import { useState } from "react";

import { HitData, PitchData, Sport } from "../../types/gameFull";
import { getStrikeZoneBall } from "../../utils/pitchTagging";
import { StrikeZone } from "../PitchTagging/StrikeZone";
import { ContactPointVis } from "../Visualizations/ContactPointVis";
import { LandingPointVis } from "../Visualizations/LandingPointVis";
import { ReleasePointVis } from "../Visualizations/ReleasePointVis";
import { SoftballLandingPointVis } from "../Visualizations/SoftballLandingPointVis";

const TABS = [
  { id: "strikeZone", label: "Strike Zone" },
  { id: "release", label: "Release" },
  { id: "contact", label: "Contact" },
  { id: "landing", label: "Landing" },
] as const;

type TabId = (typeof TABS)[number]["id"];
type Perspective = "pitcher" | "catcher";

type MetricVisualizationsProps = {
  pitchData: PitchData | null;
  hitData: HitData | null;
  sport: Sport;
};

export const MetricVisualizations = ({
  pitchData,
  hitData,
  sport,
}: MetricVisualizationsProps) => {
  const [activeTab, setActiveTab] = useState<TabId>("strikeZone");
  const [perspective, setPerspective] = useLocalStorage<Perspective>({
    key: "scorekeeping-v2-strike-zone-perspective",
    defaultValue: "pitcher",
    serialize: (value) => value,
    deserialize: (value) => (value === "catcher" ? "catcher" : "pitcher"),
  });

  const isSoftball = sport === "softball";
  const ball = getStrikeZoneBall(pitchData, perspective, isSoftball);
  const LandingVis = isSoftball ? SoftballLandingPointVis : LandingPointVis;

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Tabs */}
      <div className="flex justify-center gap-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-testid="pitch-metrics-visualization-tab"
            data-value={tab.id}
            aria-current={activeTab === tab.id ? "true" : undefined}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              // Spacing
              "pb-1",
              // Border
              "border-b-2",
              // Text
              "text-sm",
              // Effects
              "transition-colors",
              // States
              activeTab === tab.id
                ? "border-gray-900 text-gray-900 dark:border-white dark:text-white"
                : "border-transparent text-gray-900 hover:border-tagging-muted dark:text-white",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="w-full max-w-[320px]">
        {activeTab === "strikeZone" && (
          <div className="flex flex-col items-center">
            <p className="text-xs italic text-tagging-muted">
              Home plate is not to exact scale
            </p>
            <StrikeZone
              data-testid="pitch-metrics-strike-zone-chart"
              className="w-full max-w-[280px]"
              ballX={ball?.ballX}
              ballY={ball?.ballY}
              isStrike={ball?.isStrike}
              perspective={perspective}
              isSoftball={isSoftball}
            />
          </div>
        )}
        {activeTab === "release" && (
          <ReleasePointVis
            data-testid="pitch-metrics-release-point-chart"
            className="w-full"
            relHeight={pitchData?.RelHeightFeet}
            relSide={pitchData?.RelSideFeet}
          />
        )}
        {activeTab === "contact" && (
          <ContactPointVis
            data-testid="pitch-metrics-contact-point-chart"
            className="w-full"
            xFeet={hitData?.yt_HitLocationXFeet}
            yFeet={hitData?.yt_HitLocationYFeet}
          />
        )}
        {activeTab === "landing" && (
          <LandingVis
            data-testid="pitch-metrics-landing-point-chart"
            className="w-full"
            xFeet={hitData?.yt_GroundLocationXFeet}
            yFeet={hitData?.yt_GroundLocationYFeet}
          />
        )}
      </div>

      {activeTab === "strikeZone" && (
        <div className="flex w-full max-w-[400px] justify-end">
          <button
            type="button"
            data-testid="pitch-metrics-perspective-toggle"
            data-value={perspective}
            onClick={() =>
              setPerspective(perspective === "pitcher" ? "catcher" : "pitcher")
            }
            className={cn(
              // Size
              "h-8",
              // Spacing
              "px-4",
              // Border
              "rounded-full border border-tagging-option-border",
              // Text
              "text-xs font-medium text-gray-900 dark:text-white",
              // States
              "hover:border-tagging-option-selected-border/50",
            )}
          >
            {perspective === "pitcher" ? "Pitcher's POV" : "Catcher's POV"}
          </button>
        </div>
      )}
    </div>
  );
};
